import { useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera } from "@fortawesome/free-solid-svg-icons";
import { isDarkTheme } from "../../helpers";
import TourPhotoRequirements from "./TourPhotoRequirements";
import PhotoPreview from "../ChosenApp/photoModeration/PhotoPreview";
import usePhotoModerationUpload from "../../usePhotoModeration/usePhotoModerationUpload";
import TourPhotoStep from "./TourPhotoStep";
import TourPhotoSuccess from "./TourPhotoSuccess";
import TourPhotoReject from "./TourPhotoReject";
import TourUploadButton from "./TourUploadButton";
import SubmitMessage from "../Shared/SubmitMessage";

import "../../assets/css/order-confirmation.css";
import { PhraseKey } from "../../languages";

interface TourOrderPhotoUploadProps {
  visible?: boolean;
  photoModerationStep: string;
  setPhotoModerationStep: (step: string) => void;
  onPhotoReview?: (data: { imageUrl: string; imageHandshake: string }) => void;
  onPhotoConfirmed?: (data: { mediaUrl: string; mediaHandshake: string }) => void;
  onPhotoSkipped?: () => void;
  headingText: string;
  paragraphText: string;
  reviewParagraphText: string;
  showUploadedPhotoPreview?: boolean;
  eletterError?: string | null;
}

const TourOrderPhotoUpload: React.FC<TourOrderPhotoUploadProps> = (props) => {
  const {
    visible = true,
    photoModerationStep,
    setPhotoModerationStep,
    onPhotoReview,
    onPhotoConfirmed,
    onPhotoSkipped,
    headingText,
    paragraphText,
    reviewParagraphText,
    showUploadedPhotoPreview = true,
    eletterError
  } = props;
  const photoModerationParams = usePhotoModerationUpload();
  const {
    base64Photo,
    handleFileChange,
    photoModResponse,
    photoModerationLoading,
    photoModerationStatus
  } = photoModerationParams;
  const errorRef = useRef<HTMLDivElement>(null);

  const onPhotoUploadComplete = (data) => {
    if (data?.imageHandshake && data?.imageUrl) {
      setPhotoModerationStep("photo-review");

      // Notify parent immediately for preview purposes
      if (onPhotoReview) {
        onPhotoReview({
          imageUrl: data.imageUrl,
          imageHandshake: data.imageHandshake
        });
      }
    } else if (!data?.imageHandshake && !data?.photoModeration?.accepted) {
      setPhotoModerationStep("photo-rejected");
    } else {
      setPhotoModerationStep("photo-pending");
    }
  };

  const onHandleFileChange = (callback) => (event) => {
    // Prevent multiple subsequent rejected photos from entering an unrecoverable loading state.
    photoModerationParams.resetPhotoModerationState();

    // Scroll to top of the page when user uploads a photo.
    window.scrollTo(0, 0);

    // Validate and upload.
    handleFileChange(event, callback);
  };

  const onSkip = () => {
    if (onPhotoSkipped) {
      onPhotoSkipped();
    }
    setPhotoModerationStep("photo-skipped");
  };

  const onPhotoConfirm = () => {
    if (photoModResponse?.imageUrl && photoModResponse?.imageHandshake) {
      if (onPhotoConfirmed) {
        onPhotoConfirmed({
          mediaUrl: photoModResponse.imageUrl,
          mediaHandshake: photoModResponse.imageHandshake
        });
      }
    }
  };

  useEffect(() => {
    if (photoModerationLoading === true) {
      setPhotoModerationStep("photo-loading");
    }
  }, [photoModerationLoading]);

  useEffect(() => {
    if (photoModerationStatus === "error") {
      setPhotoModerationStep("photo-rejected");
    }
  }, [photoModerationStatus]);

  useEffect(() => {
    if (eletterError && photoModerationStep === "photo-review" && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [eletterError, photoModerationStep]);

  if (visible === false) {
    return null;
  }

  const ghostButtonClass = isDarkTheme() ? "btn-ghost-white" : "btn-ghost-orange";

  return (
    <div className="post-checkout-flow__wrapper">
      <TourPhotoStep visible={photoModerationStep === "photo-pending"}>
        <div className="post-checkout-flow__header">
          <h3 className="post-checkout-flow__header-text __staple--small __staple">
            {headingText}
          </h3>
        </div>

        <div className="post-checkout-flow__instructions">
          <p className="eta">{paragraphText}</p>
        </div>

        <div className="post-checkout-flow__controls">
          <TourUploadButton
            classList="btn btn-primary photo"
            clickEventActionKey="photo_upload_click"
            handleFileChange={onHandleFileChange(onPhotoUploadComplete)}>
            <FontAwesomeIcon icon={faCamera} className="icon" /> Photo
          </TourUploadButton>
          <button
            className={`btn ${ghostButtonClass}`}
            onClick={onSkip}
            data-link-info={`skip:${window.location.href}`}
            data-link-type="button"
            data-link-target=" internal_link">
            Skip
          </button>
        </div>

        <TourPhotoRequirements visible={true} />
      </TourPhotoStep>

      <TourPhotoStep visible={photoModerationStep === "photo-review"}>
        <TourPhotoSuccess
          onConfirm={onPhotoConfirm}
          onRetake={onHandleFileChange(onPhotoUploadComplete)}
          paragraphText={reviewParagraphText}>
          {showUploadedPhotoPreview && <PhotoPreview base64Photo={base64Photo} />}
        </TourPhotoSuccess>

        {eletterError && (
          <div ref={errorRef} style={{ scrollMarginBottom: "16px" }}>
            <style>
              {`
                .submit-message.alert.photo {
                  margin-top: 0;
                  margin-bottom: 0;
                }
              `}
            </style>
            <SubmitMessage
              visible={true}
              alertType="danger"
              messagePhraseKey={eletterError as PhraseKey}
              wrapperClassName=""
              className="submit-message photo"
            />
          </div>
        )}
      </TourPhotoStep>

      <TourPhotoStep visible={photoModerationStep === "photo-rejected"}>
        <TourPhotoReject
          onRetake={onHandleFileChange(onPhotoUploadComplete)}
          photoModerationParams={photoModerationParams}>
          {showUploadedPhotoPreview && <PhotoPreview base64Photo={base64Photo} />}
        </TourPhotoReject>
        <TourPhotoRequirements visible={true} />
      </TourPhotoStep>
    </div>
  );
};

export default TourOrderPhotoUpload;
